import { useEffect, useRef, useState } from "react";

function useCountUp(target, duration = 2000, started = false) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!started) return;
    let start = null;
    const step = (timestamp) => {
      if (!start) start = timestamp;
      const progress = Math.min((timestamp - start) / duration, 1);
      setCount(Math.floor(progress * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [target, duration, started]);
  return count;
}

// Updated stats with correct company data
const stats = [
  { value: 15, suffix: "+", label: "Projects Completed", icon: "🏗️" },
  { value: 5, suffix: "", label: "Years of Excellence", icon: "📅" },
  { value: 20, suffix: "+", label: "Happy Clients", icon: "🤝" },
  { value: 100, suffix: "%", label: "Client Satisfaction", icon: "⭐" },
];

// Removed ISO certification as requested
const whys = [
  { title: "Quality Commitment", desc: "Our quality management systems meet the highest industry standards." },
  { title: "On-Time Delivery", desc: "We pride ourselves on delivering projects as promised." },
  { title: "Transparent Pricing", desc: "No hidden costs. Clear, itemized quotations for every project." },
  { title: "Expert Team", desc: "Skilled engineers and architects dedicated to your vision." },
  { title: "Eco-Friendly Practices", desc: "Sustainable construction reducing environmental footprint." },
  { title: "Growing Presence", desc: "Building trust across multiple regions with quality service." },
];

export default function Stats() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const counts = [
    useCountUp(stats[0].value, 2000, visible),
    useCountUp(stats[1].value, 1500, visible),
    useCountUp(stats[2].value, 2200, visible),
    useCountUp(stats[3].value, 1800, visible),
  ];

  return (
    <section className="bg-gradient-to-br from-red-950 via-blue-950 to-blue-900 py-28 relative overflow-hidden">
      {/* Decorative */}
      <div className="absolute top-0 right-0 w-80 h-80 bg-red-600/10 rounded-full blur-3xl"/>
      <div className="absolute bottom-0 left-0 w-96 h-40 bg-blue-500/5 rounded-full blur-3xl"/>
      <div className="absolute top-1/2 left-1/4 w-64 h-64 bg-red-500/5 rounded-full blur-3xl"/>

      <div className="max-w-7xl mx-auto px-6">
        {/* Stats row */}
        <div ref={ref} className="grid grid-cols-2 lg:grid-cols-4 gap-8 mb-24">
          {stats.map((s, i) => (
            <div key={s.label} className="text-center group">
              <div className="text-4xl mb-3">{s.icon}</div>
              <div className="text-white font-bold"
                style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: '3.5rem', lineHeight: 1 }}>
                {counts[i]}{s.suffix}
              </div>
              <div className="text-red-300/70 text-sm tracking-widest uppercase mt-2"
                style={{ fontFamily: "'DM Sans', sans-serif" }}>
                {s.label}
              </div>
            </div>
          ))}
        </div>

        {/* Why Us */}
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <div className="inline-flex items-center gap-2 text-red-400 text-xs font-semibold tracking-widest uppercase
              bg-red-600/10 border border-red-600/20 px-4 py-2 rounded-full mb-6">
              Why NexEdge
            </div>
            <h2 style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 700, fontSize: 'clamp(2rem,4vw,3rem)' }}
              className="text-white mb-4">
              SETTING THE STANDARD<br/><span className="text-red-400">IN INFRASTRUCTURE</span>
            </h2>
            <p className="text-blue-200/60 text-base leading-relaxed max-w-md" style={{ fontFamily: "'DM Sans', sans-serif" }}>
              NexEdge Infra is your trusted partner for quality infrastructure solutions, building excellence across every project we undertake.
            </p>
            <a href="#contact"
              className="mt-8 inline-flex items-center gap-3 bg-gradient-to-r from-red-900 to-blue-900 hover:from-red-800 hover:to-blue-800 text-white
              px-8 py-4 rounded-xl font-semibold transition-all duration-300 shadow-xl shadow-red-900/30">
              Work With Us
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3"/>
              </svg>
            </a>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {whys.map((w, i) => (
              <div key={w.title}
                className="glass-card rounded-xl p-5 hover:bg-white/10 transition-all duration-300 group">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-gradient-to-br from-red-600 to-blue-600 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7"/>
                    </svg>
                  </div>
                  <div>
                    <h4 className="text-white font-semibold text-sm mb-1">{w.title}</h4>
                    <p className="text-blue-300/60 text-xs leading-relaxed">{w.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}